<template>
    <div data-testid="step-confirmPhone" class="container step-confirm-phone">
        <h1 class="container__standard-header">
            {{ $t(`${TRANSLATION_KEY}.phoneNumber`) }}
        </h1>
        <form v-if="!isSmsSent" @submit.prevent="() => {}">
            <p class="step-confirm-phone__info">
                {{ $t(`${TRANSLATION_KEY}.phoneVerify`) }}
                {{ $t(`${TRANSLATION_KEY}.phoneVerifyInfo`) }}
            </p>
            <div
                class="row row--account-validation-get-data"
                :style="{ 'margin-bottom': isSmsSent ? '0' : '-25px' }"
            >
                <PhoneField
                    ref="phoneNumberInput"
                    v-model="phone"
                    name="phoneNumber"
                    cy-data="phone-number"
                    :placeholder="
                        $t(`${TRANSLATION_KEY}.phoneNumberPlaceholder`)
                    "
                    @update:model-value="($event) => updatePhone($event)"
                />
            </div>
            <RecaptchaButton
                :action="Action.DESKTOP_SMS"
                :data="formData"
                :button-value="$t(`${TRANSLATION_KEY}.confirmPhoneNumber`)"
                data-testid="btn-confirmPhone-confirmPhoneNumber"
                class="button submit-button"
                :style="{ top: isSmsSent ? '0' : '25px' }"
                :disabled="formData.phoneNumber.length < 5"
                @success-submit="handleSubmitSuccess"
            />
        </form>

        <!--- Second form is visible when first form was sent with data like (directional | phone number) to backend --->
        <form v-if="isSmsSent" id="confirm-phone" @submit.prevent="() => {}">
            <p class="step-confirm-phone__info">
                {{ $t(`${TRANSLATION_KEY}.phoneVerify`) }}
                {{ $t(`${TRANSLATION_KEY}.phoneVerifyInfo`) }}
            </p>
            <div class="row row--account-validation-get-data">
                <div class="row row--account-validation-get-data__phone-number">
                    <PhoneField
                        ref="phoneNumberInput"
                        v-model="phone"
                        name="phoneNumber"
                        cy-data="phone-number"
                        :placeholder="
                            $t(`${TRANSLATION_KEY}.phoneNumberPlaceholder`)
                        "
                        :disabled="true"
                        :class="{ 'phone-disabled': isSmsSent }"
                    />
                </div>
                <div class="row--account-validation-get-sms-code">
                    <div class="row--account-validation-get-sms-code__input">
                        <Field
                            v-model="formData.phoneVerificationCode2"
                            type="text"
                            name="phoneVerificationCode2"
                            :placeholder="$t(`${TRANSLATION_KEY}.smsCode`)"
                            cy-data="sms-code"
                            :is-numeric="true"
                        >
                            <span>
                                {{ $t(`${TRANSLATION_KEY}.smsCode`) }}
                            </span>
                        </Field>
                    </div>
                </div>
            </div>

            <div class="confirm-phone-actions">
                <RecaptchaButton
                    :action="Action.DESKTOP_SMS"
                    data-testid="btn-confirmPhone-resendCode"
                    :data="formData"
                    button-type="button"
                    class="resend-code"
                    @success-submit="handleSubmitSuccess"
                >
                    {{ $t(`${TRANSLATION_KEY}.didntGetSmsOne`) }}
                    <strong
                        >{{
                            $t(`${TRANSLATION_KEY}.didntGetSmsTwo`)
                        }}
                        &raquo;</strong
                    >
                </RecaptchaButton>
                <SubmitButton
                    :action="Action.CONFIRM_SMS_CODE"
                    :data="formData"
                    data-testid="btn-confirmPhone-confirm"
                    class="button submit-button"
                    :style="{ 'margin-bottom': isSmsSent ? '-11px' : '0' }"
                    :disabled="!formData.phoneVerificationCode2.length"
                    @click="
                        () =>
                            amplitudeSendEvent(
                                AmplitudeAnalyticsEvents.phoneNumber
                                    .PHONE_NUMBER_SMS_CONTINUE
                            )
                    "
                >
                    {{ $t(`${TRANSLATION_KEY}.confirm`) }}
                </SubmitButton>

                <RecaptchaButton
                    v-if="formData.sentSmsCount >= 2"
                    :action="Action.VERIFY_EMAIL"
                    data-testid="btn-confirmPhone-verificationByEmail"
                    :data="formData"
                    button-type="button"
                    class="verification-by-email"
                >
                    {{ $t(`${TRANSLATION_KEY}.unableToVerifyBySmsOne`) }} -
                    <strong
                        >{{
                            $t(`${TRANSLATION_KEY}.unableToVerifyBySmsTwo`)
                        }}
                        &raquo;</strong
                    >
                </RecaptchaButton>

                <button
                    type="button"
                    class="fix-phone-number"
                    @click="isSmsSent = false"
                >
                    {{ $t(`${TRANSLATION_KEY}.wrongPhoneOne`) }} -
                    <strong
                        >{{
                            $t(`${TRANSLATION_KEY}.wrongPhoneTwo`)
                        }}
                        &raquo;</strong
                    >
                </button>
            </div>
        </form>
    </div>
</template>

<script lang="ts">
import {
    defineComponent,
    onMounted,
    onBeforeUnmount,
    ref,
    reactive,
} from 'vue';
import Field from '@/components/basic/fields/Field.vue';
import NamedButton from '@/components/basic/buttons/NamedButton.vue';
import SubmitButton from '@/components/basic/buttons/SubmitButton.vue';
import RecaptchaButton from '@/components/basic/buttons/RecaptchaSubmitButton.vue';
import PhoneField from '@/components/compositeFields/PhoneField.vue';
import { UtilityHelper } from '@/helpers/UtilityHelper';
import PhonePrefixSelect from '../compositeFields/PhonePrefixSelect.vue';
import TheCountrySelect from '../basic/fields/TheCountrySelect.vue';
import { useStore } from 'vuex';
import {
    AmplitudeAnalyticsEvents,
    amplitudeSendEvent,
} from '@/utils/AmplitudeAnalyticsEvents';
import { SET_PROGRESS } from '@/constants/mutation-types';
import { PROGRESS_BAR_TIMES } from '@/constants/progress-bar-times';
import { Action } from '@/constants/ajax';

export default defineComponent({
    name: 'AccountValidation',
    components: {
        Field,
        NamedButton,
        SubmitButton,
        PhoneField,
        RecaptchaButton,
        PhonePrefixSelect,
        TheCountrySelect,
    },
    setup() {
        const isExpanded = ref(false);
        const isSmsSent = ref(false);
        const phoneChanged = ref(false);
        const documentListener = ref<(evt: MouseEvent) => void>(() => {});
        const store = useStore();

        const phone = reactive({
            phone: '',
            phonePrefix: '',
        });
        const formData = reactive({
            phoneNumber: '',
            phoneNumberPrefix: '',
            phoneVerificationCode2: '',
            recaptchaToken: '',
            sentSmsCount: 0,
        });
        const TRANSLATION_KEY = 'steps.ConfirmPhone';

        const phoneNumberInput = ref(null);

        const updatePhone = (value: any) => {
            formData.phoneNumber = value.phone;
            formData.phoneNumberPrefix = value.phonePrefix;
        };

        const handleSubmitSuccess = () => {
            isSmsSent.value = true;
            formData.sentSmsCount++;
            amplitudeSendEvent(
                AmplitudeAnalyticsEvents.phoneNumber.PHONE_NUMBER_CONTINUE
            );
            amplitudeSendEvent(
                AmplitudeAnalyticsEvents.phoneNumber.PHONE_NUMBER_SMS
            );
        };

        onMounted(() => {
            store.commit(SET_PROGRESS, PROGRESS_BAR_TIMES.CONFIRM_PHONE);

            documentListener.value = (evt: MouseEvent) => {
                const target = evt.target as HTMLElement | null;
                if (phoneNumberInput.value) {
                    const refEl = (phoneNumberInput.value as any)
                        .$el as HTMLElement;
                    if (target && isExpanded.value) {
                        if (
                            !target.classList.contains('vue-tel-input') &&
                            !target.classList.contains('vti__dropdown') &&
                            !target.classList.contains('vti__input') &&
                            !target.classList.contains('vti__selection')
                        ) {
                            refEl.classList.remove('modifier-vue-tel-input');
                            refEl.classList.add('default-vue-tel-input');
                            isExpanded.value = false;
                        }
                        if (target.classList.contains('vti__input')) {
                            refEl.classList.remove('modifier-vue-tel-input');
                            refEl.classList.add('default-vue-tel-input');
                            isExpanded.value = false;
                        }
                    }
                }
            };
            document.addEventListener('click', documentListener.value);
        });

        onBeforeUnmount(() => {
            document.removeEventListener('click', documentListener.value);
        });

        return {
            isExpanded,
            isSmsSent,
            phoneChanged,
            phone,
            formData,
            TRANSLATION_KEY,
            Action,
            UtilityHelper,
            phoneNumberInput,
            updatePhone,
            handleSubmitSuccess,
            amplitudeSendEvent,
            AmplitudeAnalyticsEvents,
        };
    },
});
</script>

<style scoped>
.step-confirm-phone__info {
    margin-top: 0;
    margin-bottom: 30px;
    font-size: 13px;
    font-weight: 700;
    text-align: center;
}

.phone-disabled {
    opacity: 0.7;
}

.phone-disabled :deep(*) {
    cursor: not-allowed !important;
}
</style>
